import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Role } from '../../types';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';

interface EditRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: Partial<Role>) => Promise<void>;
  role?: Partial<Role> | null;
  title: string;
}

const RoleSchema = Yup.object().shape({
  title: Yup.string().required('Role title is required'),
  ownerName: Yup.string().nullable(),
  subtitle: Yup.string().nullable(),
  responsibilities: Yup.array().of(Yup.string()),
});

export const EditRoleModal = ({
  isOpen,
  onClose,
  onSave,
  role,
  title,
}: EditRoleModalProps) => {
  const initialValues = {
    title: role?.title || '',
    ownerName: role?.ownerName || '',
    subtitle: role?.subtitle || '',
    responsibilities: role?.responsibilities && role.responsibilities.length > 0
      ? role.responsibilities
      : [''],
    // parentId and level are managed by the page, not this form
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Formik
        initialValues={initialValues}
        validationSchema={RoleSchema}
        onSubmit={async (values, { setSubmitting }) => {
          // Filter out empty responsibilities
          const filteredResponsibilities = values.responsibilities.filter(
            (resp) => resp.trim() !== ''
          );
          await onSave({
            ...values,
            responsibilities: filteredResponsibilities,
          });
          setSubmitting(false);
          onClose();
        }}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form>
            <ModalContent>
              <ModalHeader>{title}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <Field name="title">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={!!errors.title && touched.title}>
                        <FormLabel>Role Title</FormLabel>
                        <Input {...field} placeholder="e.g., Technology, Operations" />
                        <FormErrorMessage>{errors.title}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="ownerName">
                    {({ field }: any) => (
                      <FormControl>
                        <FormLabel>Owner Name</FormLabel>
                        <Input {...field} placeholder="e.g., Jane Doe" />
                      </FormControl>
                    )}
                  </Field>
                  <Field name="subtitle">
                    {({ field }: any) => (
                      <FormControl>
                        <FormLabel>Subtitle/Designation</FormLabel>
                        <Input {...field} placeholder="e.g., LMA, CEO" />
                      </FormControl>
                    )}
                  </Field>

                  <FormControl>
                    <FormLabel>Responsibilities</FormLabel>
                    <FieldArray name="responsibilities">
                      {({ push, remove }: any) => (
                        <VStack align="stretch" spacing={2}>
                          {values.responsibilities.map((_, index) => (
                            <HStack key={index}>
                              <Field name={`responsibilities.${index}`}>
                                {({ field }: any) => (
                                  <Input
                                    {...field}
                                    placeholder="Enter responsibility"
                                  />
                                )}
                              </Field>
                              {values.responsibilities.length > 1 && (
                                <IconButton
                                  aria-label="Remove responsibility"
                                  icon={<DeleteIcon />}
                                  onClick={() => remove(index)}
                                />
                              )}
                            </HStack>
                          ))}
                          <Button
                            size="sm"
                            leftIcon={<AddIcon />}
                            onClick={() => push('')}
                            variant="outline"
                          >
                            Add Responsibility
                          </Button>
                        </VStack>
                      )}
                    </FieldArray>
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Save Role
                </Button>
              </ModalFooter>
            </ModalContent>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

